// Copyright ©️ 2026 eVolve MEP, LLC

import '@xyflow/react/dist/style.css';

import { useCallback, useMemo, useState } from 'react';

import { ActionIcon, Button, Card, Flex, LoadingOverlay, Text, useMantineTheme, Divider } from '@mantine/core';
import { applyNodeChanges, MarkerType, type Node, type NodeChange, ReactFlow, useReactFlow } from '@xyflow/react';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useBlockerWhen } from 'hooks/navigation/useBlockerWhen';
import { AddPartToAssembly } from 'modules/Shop/CatalogSetup/PartEditor/AddPartToAssembly';
import { AddTaskToAssembly } from 'modules/Shop/CatalogSetup/PartEditor/AddTaskToAssembly';
import { assemblyNodeTypes, type NodeType } from 'modules/Shop/CatalogSetup/PartEditor/nodes/types';
import { useAssemblyEditor } from 'modules/Shop/CatalogSetup/PartEditor/useAssemblyEditor';
import {
  useCalculatePartNodeStartingPoint,
  useCalculateTaskNodeStartingPoint,
} from 'modules/Shop/CatalogSetup/PartEditor/utils';

const PublishButton = () => {
  const { isDirty, form, publishing } = useAssemblyEditor();
  return (
    <Button type="submit" disabled={!isDirty || !form.isValid()} loading={publishing}>
      Publish assembly
    </Button>
  );
};

export const AssemblyEditorView = () => {
  const theme = useMantineTheme();
  const { fitView, zoomIn, zoomOut, deleteElements } = useReactFlow();
  const {
    catalogPart: part,
    publishing,
    isDirty,
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    addNode,
    catalogSetupUrl,
    goBackToCatalog,
  } = useAssemblyEditor();

  const { calculatePartNodeStartingPoint } = useCalculatePartNodeStartingPoint();
  const { calculateTaskNodeStartingPoint } = useCalculateTaskNodeStartingPoint();

  const selected = useMemo(() => nodes.find((n) => n.selected) ?? edges.find((e) => e.selected), [edges, nodes]);

  // Used to manage dragging separately,
  // so that dragging doesn't cause a rerender of everything downstream
  const [draggingState, setDraggingState] = useState<Node[]>();
  const [lastChange, setLastChange] = useState<NodeChange<Node>[]>();

  const onNodesChangeWrapped = useCallback<typeof onNodesChange>(
    (changes) => {
      if (isNotNil(draggingState)) {
        setLastChange(changes);
        setDraggingState((nds) => applyNodeChanges(changes, nds ?? []));
      } else {
        onNodesChange(changes);
      }
    },
    [draggingState, onNodesChange],
  );

  useBlockerWhen('assembly-editor', isDirty);

  return (
    <>
      <BasePageHeader
        title={part?.name ?? 'New Assembly'}
        parentPage={{
          title: 'Catalog Setup',
          url: catalogSetupUrl,
        }}
        topRightComponent={
          <>
            <AddPartToAssembly />
            <AddTaskToAssembly />
            <Divider orientation="vertical" />
            <Button variant="light" disabled={publishing} onClick={goBackToCatalog}>
              Cancel
            </Button>
            <PublishButton />
          </>
        }
      />

      <Card withBorder style={{ width: '100%', height: '100%' }}>
        <ReactFlow
          nodeTypes={assemblyNodeTypes}
          nodes={draggingState ?? nodes}
          onNodesChange={onNodesChangeWrapped}
          onNodeDragStart={() => setDraggingState(nodes)}
          onNodeDragStop={() => {
            setDraggingState(undefined);
            onNodesChange(lastChange ?? []);
          }}
          edges={edges}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          proOptions={{ hideAttribution: true }}
          defaultEdgeOptions={{
            deletable: true,
            markerEnd: { type: MarkerType.ArrowClosed, color: '#666' },
            type: 'smoothstep',
            style: { strokeWidth: 2 },
          }}
          fitView
          minZoom={0.2}
          fitViewOptions={{ maxZoom: 1, padding: 0 }}
          snapGrid={[1, 1]}
          snapToGrid
        />

        <Card
          p={6}
          withBorder
          style={{
            position: 'absolute',
            right: 10,
            bottom: 10,
          }}
        >
          <Flex direction="column" gap="xs">
            <AgGridStyleTooltip label="Zoom in" withArrow openDelay={200} position="left">
              <ActionIcon
                size="lg"
                color={theme.primaryColor}
                variant="light"
                onClick={() => zoomIn({ duration: 300 })}
              >
                <EvolveIcon icon="Add" size="sm" />
              </ActionIcon>
            </AgGridStyleTooltip>
            <AgGridStyleTooltip label="Zoom out" withArrow openDelay={200} position="left">
              <ActionIcon
                size="lg"
                color={theme.primaryColor}
                variant="light"
                onClick={() => zoomOut({ duration: 300 })}
              >
                <Text fz="xl" fw={600}>
                  —
                </Text>
              </ActionIcon>
            </AgGridStyleTooltip>
            <AgGridStyleTooltip label="Zoom view to fit" withArrow openDelay={200} position="left">
              <ActionIcon
                size="lg"
                color={theme.primaryColor}
                variant="light"
                onClick={() => fitView({ duration: 500, padding: 0 })}
              >
                <EvolveIcon icon="CollapsePane" />
              </ActionIcon>
            </AgGridStyleTooltip>
          </Flex>
        </Card>
        <Card
          p={6}
          withBorder
          style={{
            position: 'absolute',
            left: 10,
            bottom: 10,
          }}
        >
          <Flex direction="column" gap="xs">
            <AgGridStyleTooltip
              label={`Duplicate selected ${selected?.type}`}
              withArrow
              openDelay={200}
              position="right"
            >
              <ActionIcon
                size="lg"
                color={theme.primaryColor}
                disabled={isNil(selected) || selected.type === 'assembly' || !('position' in selected)}
                variant="light"
                onClick={() => {
                  if (isNil(selected) || !('position' in selected)) return;
                  const position =
                    selected.type === 'part' ? calculatePartNodeStartingPoint() : calculateTaskNodeStartingPoint();
                  const newNode = addNode({
                    ...selected,
                    position,
                  } as NodeType);
                  onNodesChange([
                    {
                      id: selected.id,
                      type: 'select',
                      selected: false,
                    },
                    {
                      id: newNode.id,
                      type: 'select',
                      selected: true,
                    },
                  ]);
                }}
              >
                <EvolveIcon icon="Copy" />
              </ActionIcon>
            </AgGridStyleTooltip>
            <AgGridStyleTooltip
              label={`Delete selected ${selected?.type ?? 'edge'}`}
              withArrow
              openDelay={200}
              position="right"
            >
              <ActionIcon
                size="lg"
                color="red"
                disabled={isNil(selected) || selected.type === 'assembly'}
                variant="light"
                onClick={() => {
                  if (isNil(selected)) return;
                  void deleteElements({
                    nodes: [selected],
                    edges: [selected],
                  });
                }}
              >
                <EvolveIcon icon="Delete" />
              </ActionIcon>
            </AgGridStyleTooltip>
          </Flex>
        </Card>
        <LoadingOverlay visible={publishing} />
      </Card>
    </>
  );
};
